import { GetStaticProps } from 'next';
import React from 'react';

import { RegioExpresNewsOverviewSingleFieldsFragment, RegioExpresInformationFieldsFragment } from 'api/graphql-types';
import client, { httpLink, setPreviewSecret } from 'api/apollo-client';
import { getServerPageGetRegioExpresNewsOverview } from 'api/graphql-helper';

import Layout from 'modules/layout/layout';
import { ModuleBuilder } from 'modules/module-builder/module-builder';
import { Head } from 'modules/head/head';
import IntroHeader from 'modules/intro-header/intro-header';

import { initialState as settingsInitialState } from 'redux/stores/settings';
import { initialState as newsInitialState } from 'redux/stores/news';
import { getStrapiParametersFromContext } from 'utils/strapi-parameters';
import { IBasePageProps } from 'types/page';
import { REGIO_EXPRESS_ROUTES } from 'constants/routes';
import { searchNewsArticles } from 'api/algolia';
import { NEWS_ARTICLES_OVERVIEW_LIMIT } from 'modules/news-overview/data';

interface IRegioExpresNewsOverviewPageProps extends IBasePageProps {
	newsOverview?: RegioExpresNewsOverviewSingleFieldsFragment;
	pageInformation?: RegioExpresInformationFieldsFragment;
}

const RegioExpresNewsOverviewPage = (props: IRegioExpresNewsOverviewPageProps): React.JSX.Element => {
	const modules = props.newsOverview?.modules || [];
	const seoModule = props?.newsOverview?.seo;

	return (
		<Layout navbar={props?.pageInformation?.navbar} footer={props?.pageInformation?.footer}>
			<Head seoModule={seoModule} seoParent={props.pageInformation?.seo} />
			<IntroHeader module={props?.newsOverview?.header} slug={REGIO_EXPRESS_ROUTES.NEWS.fullPath} />
			<ModuleBuilder modules={modules} slug={REGIO_EXPRESS_ROUTES.NEWS.fullPath} />
		</Layout>
	);
};

export const getStaticProps: GetStaticProps<IBasePageProps> = async (ctx) => {
	try {
		const { locale, publicationState, draftMode } = getStrapiParametersFromContext(ctx);

		if (publicationState === 'DRAFT') {
			client.setLink(setPreviewSecret(process.env.STRAPI_PREVIEW_SECRET as string).concat(httpLink));
		}

		const result = await getServerPageGetRegioExpresNewsOverview({ variables: { locale, publicationState } }, client);
		const { regioExpresInformation, regioExpresNewsOverview } = result.props.data;

		const { results: newsArticles, total: newsArticlesCount } = await searchNewsArticles(
			'',
			{
				themesIds: []
			},
			{
				limit: NEWS_ARTICLES_OVERVIEW_LIMIT,
				page: 1,
				sort: 'desc',
				locale
			},
			'regio-expres'
		);

		if (regioExpresNewsOverview == null) {
			return {
				notFound: true
			};
		}

		return {
			props: {
				pageInformation: regioExpresInformation?.data?.attributes,
				newsOverview: regioExpresNewsOverview?.data?.attributes,
				initialReduxState: {
					settings: {
						...settingsInitialState,
						publicationState,
						draftMode,
						contentType: 'regio-expres-news-overview',
						contentTypeId: regioExpresNewsOverview?.data?.documentId,
						showNeutralStyle: true,
						hideLanguageSwitcher: true,
						isSubsite: true
					},
					news: {
						...newsInitialState,
						newsArticles,
						newsArticlesCount
					}
				}
			},
			revalidate: Number(process.env.NEXT_PUBLIC_REVALIDATE_TIME_IN_SECONDS)
		};
	} catch (error: any) {
		// eslint-disable-next-line no-console
		console.error('Error in regio expres news overview', error);

		return {
			notFound: true
		};
	}
};

export default RegioExpresNewsOverviewPage;
